import React, { FC, useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import Button from '../core/Button';
import Tooltip from '../Tooltip';
import { useErrorReportsMutations } from '../../hooks/useErrorReports';
import { IconTool } from '../../icons';
import { dateFormat } from '../../helpers/datetime_helpers';
import { useUserLookup } from '../../hooks/useUserLookup';

const messages = defineMessages({
  unknown_user: {
    id: 'error_report.unknown_user',
    defaultMessage: 'Unknown user',
  },
});

interface ErrorReportDetailsProps {
  errorReport: ErrorReport;
  entityLookup: Record<string, Entity>;
  schemaDefinition: EntitySchemaDefinition;
  onClear: () => void;
}

const ErrorReportDetails: FC<ErrorReportDetailsProps> = ({
  errorReport,
  entityLookup,
  schemaDefinition,
  onClear,
}) => {
  const intl = useIntl();
  const { userLookup } = useUserLookup();
  const {
    remove: {
      mutateAsync: removeErrorReport,
      isLoading: removingErrorReportInProgress,
    },
  } = useErrorReportsMutations();

  const onRemove = useCallback(() => {
    void removeErrorReport(errorReport.id).then(() => {
      onClear();
    });
  }, [errorReport.id, onClear, removeErrorReport]);

  const entity = entityLookup[errorReport.entity_id];
  const prop = schemaDefinition.propertiesLookup[errorReport.property_key];
  if (!prop || !entity) {
    return null;
  }
  const user = userLookup(errorReport.meta.created_by);
  const userName =
    user && user.meta && !user.meta.deleted_at
      ? user.name
      : intl.formatMessage(messages.unknown_user);
  const group =
    typeof prop.group_id === 'number'
      ? schemaDefinition.groupsLookup[prop.group_id]
      : undefined;
  const propName = group ? `${group.name}: ${prop.name}` : prop.name;
  const { stop_letter } = entity;
  return (
    <div className="flex h-full flex-col">
      <div className="grow">
        <h3 className="mb-2">
          <FormattedMessage
            id="error_report.error_report_for_x"
            defaultMessage="Error report for stop {stop_letter}"
            values={{
              stop_letter,
            }}
          />
        </h3>
        <p className="text-xs leading-4 tracking-[0.5px]">
          <FormattedMessage
            id="error_report.error_report_regards"
            defaultMessage="Error report regards"
          />
        </p>
        <p className="mb-2 text-base leading-6">{propName}</p>
        <p className="text-xs leading-4 tracking-[0.5px]">
          <FormattedMessage
            id="error_report.error_report_created"
            defaultMessage="Error report created"
          />
        </p>
        <p className="mb-2 text-base leading-6">
          <FormattedMessage
            id="error_report.at_by"
            defaultMessage="{createdAt} by {userName}"
            values={{
              createdAt: errorReport.meta.created_at.toFormat(dateFormat),
              userName,
            }}
          />
        </p>
        <p className="text-xs leading-4 tracking-[0.5px]">
          <FormattedMessage
            id="error_report.error_report_opened_to"
            defaultMessage="Error report opened to"
          />
        </p>
        <p className="mb-2 text-base leading-6">{errorReport.destination}</p>
        <p className="text-xs leading-4 tracking-[0.5px]">
          <FormattedMessage
            id="error_report.comment"
            defaultMessage="Comment"
          />
        </p>
        <p className="mb-2 text-base leading-6">{errorReport.message}</p>
      </div>
      <div className="flex flex-row justify-end gap-3 pt-3">
        <Tooltip
          side="left"
          label={
            <FormattedMessage
              id="error_report.confirm_repairs_tooltip"
              defaultMessage="Click to confirm repairs have been completed. This will clear the error report."
            />
          }
        >
          <Button
            loading={removingErrorReportInProgress}
            startIcon={<IconTool />}
            disabled={removingErrorReportInProgress}
            onClick={onRemove}
            variant="filled"
          >
            <FormattedMessage
              id="error_report.confirm_repairs"
              defaultMessage="Mark as repaired"
            />
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};

export default ErrorReportDetails;
